import { css, type CSSObject } from "@emotion/react";
import {
  constants,
  typographyStyles,
  visuallyHiddenStyles,
} from "@newsela/angelou";

const { textMObj, heading5Obj, fontFamily } = typographyStyles;

export const visuallyHidden = css(visuallyHiddenStyles);

const coreBlue = { 700: "#0052C4", 500: "#106FF3" };
const white = constants.colors.ui.white[500];
const neutral400 = constants.colors.ui.neutral[400];

// --- Reusable style fragments ---
const flexColumnCenter: CSSObject = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const flexRowSpaceBetween: CSSObject = {
  display: "flex",
  justifyContent: "space-between",
};

const flexRowAlignStart: CSSObject = {
  display: "flex",
  gap: "1em",
  width: "100%",
  marginBottom: "1em",
  alignItems: "flex-start",
};

const boldHeadingText: CSSObject = {
  fontWeight: "bold",
  ...fontFamily.circular,
};

const borderBottomBlue: CSSObject = {
  borderBottom: `2px solid ${coreBlue[500]}`,
};

const gridDetails: CSSObject = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "1vw",
  marginTop: "0.5em",
};

const textareaBase: CSSObject = {
  width: "100%",
  ...textMObj,
};

const mainIdeaBoxBase: CSSObject = {
  borderRadius: 50,
  padding: "1em",
  paddingTop: 0,
  border: `4px solid ${coreBlue[500]}`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

// --- Layout grid ---
export const mainIdeaGrid = css({
  display: "grid",
  gridTemplateAreas: `
    "title"
    "mainIdea"
    "details"
  `,
  gridTemplateRows: "0.15fr 0.25fr 1fr",
  gridTemplateColumns: "1fr",
  maxHeight: "700px",
  aspectRatio: 11 / 8.4,
  background: white,
  padding: "16px",
  height: "100%",
  width: "100%",
  margin: "0 auto",
});

// --- Heading section ---
export const headingCss = css({
  ...flexRowSpaceBetween,
  marginBottom: "-10px",
});

export const iconAndTitleSection = css({
  ...flexRowAlignStart,
});

export const titleArea = css({
  gridArea: "title",
  ...flexColumnCenter,
  gap: "0.5em",
});

export const articleTitleArea = css({
  ...boldHeadingText,
  textAlign: "center",
  width: "100%",
  ...textMObj,
  color: coreBlue[700],
});

// --- Name/Class/Date section ---
export const nameClassDateSection: CSSObject = {
  width: "100%",
};

export const nameClassDateRow: CSSObject = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "5px",
  gap: "1em",
};

export const smallText: CSSObject = {
  margin: "5px 0",
  ...borderBottomBlue,
  fontSize: "0.6em",
  width: "100%",
};

// --- Directions section ---
export const directionsArea = css({
  gridArea: "directions",
  fontSize: "1.1em",
  textAlign: "center",
  marginBottom: "0.5em",
});

// --- Main Idea section ---
export const mainIdeaArea = css({
  gridArea: "mainIdea",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const mainIdeaBox = css({
  ...mainIdeaBoxBase,
  "& > span, & > label": {
    display: "flex",
    justifyContent: "center",
    padding: "0.5em",
    justifySelf: "center",
    margin: "0 auto",
    ...heading5Obj,
    ...boldHeadingText,
    borderBottom: `4px solid ${coreBlue[500]}`,
    width: "80%",
  },
});

export const mainIdeaTextarea = css({
  ...textareaBase,
  height: "85%",
  minHeight: "60px",
  padding: "0.5em",
  resize: "none",
  borderRadius: "8px",
  "&::placeholder": {
    color: neutral400,
  },
});

export const mainIdeaParagraph = css({
  ...textareaBase,
  height: "85%",
  minHeight: "60px",
  padding: "0.5em",
  resize: "none",
  borderRadius: "8px",
  margin: 0,
});

export const characterCount = css({
  alignSelf: "flex-end",
  fontSize: "0.8em",
  color: neutral400,
  marginTop: "0.25em",
  marginRight: "0.5em",
});

export const mainIdeaTextDisplay = css({
  ...boldHeadingText,
  width: "80%",
  textAlign: "center",
  ...textMObj,
});

// --- Details section ---
export const detailsArea = css({
  gridArea: "details",
  ...gridDetails,
});

export const detailsRow = css({
  ...gridDetails,
});

export const detailBox = css([
  mainIdeaBoxBase,
  {
    padding: "0.5em",
    display: "flex",
    height: "100%",
    minHeight: "150px",
    label: {
      ...boldHeadingText,
    },
    textarea: {
      minHeight: "120px",
    },
  },
]);
